<script setup lang="ts">
import type { ResourceType } from '@/backend/types'
import type {
  AssignableRole,
  ResourceAccessType,
  ResourceMember,
} from '@/modules/IdentityAndAccess/types'
import { useAllowedRoles } from '@/modules/IdentityAndAccess/useAllowedRoles'
import { useUser } from '@/modules/IdentityAndAccess/useUser'
import { getResourceMemberDisplayName } from '@/modules/IdentityAndAccess/utils'
import DividerLine from '@/uiKit/DividerLine.vue'
import IconButton from '@/uiKit/IconButton.vue'
import type { IconName } from '@/uiKit/IconName'
import IconSprite from '@/uiKit/IconSprite.vue'
import ModalDialog from '@/uiKit/ModalDialog.vue'
import { computed } from 'vue'
import DefaultPermission from './DefaultPermission.vue'
import PermissionsDialogInviteForm from './PermissionsDialogInviteForm.vue'
import PermissionsDialogMember from './PermissionsDialogMember.vue'
import { PERMISSIONS_DIALOG_ID } from './consts'

/**
 * This is a dialog used to manage the permissions for a range of resources (folders,
 * agents, cases). It is used to:
 * - Invite new workspace members to the resource
 * - See the list of current members and their roles
 * - Update the role of an existing member
 * - Update the default role for the resource
 */

const props = defineProps<{
  open: boolean
  members: ResourceMember[]
  heading?: {
    title: string
    icon: IconName
  }
  disableClose?: boolean
  accessType: ResourceAccessType | null
  resourceType: ResourceType
}>()

defineEmits<{
  'change:user-role': [userId: string, role: AssignableRole | null]
  invite: [userIds: string[], role: AssignableRole]
  'change:access-type': [accessType: ResourceAccessType]
  close: []
}>()

/** For a11y - HTML ID for the dialog's label */
const DIALOG_LABEL_ID = 'permissions-dialog-label'

const userStore = useUser()

const allowedInviteRoles = useAllowedRoles({
  permissionName: 'invite_members',
  scope: props.resourceType,
  rolesSubset: ['reader', 'editor'],
})

const allowedUpdateRoles = useAllowedRoles({
  permissionName: 'update_members',
  rolesSubset: ['reader', 'editor'],
  scope: props.resourceType,
})

/** We want to sort alphabetically, but with the resource's owner at the top. */
const sortedMembers = computed(() => {
  return props.members.toSorted((a, b) => {
    if (a.role === 'owner') return -1
    if (b.role === 'owner') return 1

    const aName = getResourceMemberDisplayName(a)
    const bName = getResourceMemberDisplayName(b)

    return aName.localeCompare(bName)
  })
})
</script>

<template>
  <ModalDialog
    :id="PERMISSIONS_DIALOG_ID"
    :open="open"
    placement="right"
    class="overflow-hidden"
    role="dialog"
    :outline="false"
    :aria-labelledby="DIALOG_LABEL_ID"
    @close="$emit('close')"
  >
    <div class="h-full">
      <div
        class="relative flex h-full w-[400px] flex-col overflow-hidden rounded-corner-10 border-0 bg-surface-tertiary-persist"
      >
        <div class="relative flex size-full flex-col justify-stretch gap-2">
          <IconButton
            size="lg"
            variant="transparent"
            class="absolute right-2 top-2 text-text-subtle"
            icon="close"
            aria-label="Close"
            rounded
            role="button"
            :disabled="disableClose"
            @click="$emit('close')"
            @mousedown.prevent
          />

          <div class="p-4 pb-1.5">
            <slot
              name="heading"
              :dialog-id="PERMISSIONS_DIALOG_ID"
              :dialog-label-id="DIALOG_LABEL_ID"
            >
              <div v-if="heading">
                <div>
                  <div
                    class="mb-3 flex size-14 items-center justify-center rounded-full bg-background-gray-subtlest"
                  >
                    <IconSprite
                      :icon="heading.icon"
                      class="text-icon-subtle"
                    />
                  </div>
                  <h2
                    :id="DIALOG_LABEL_ID"
                    class="text-display-xs-20px-default text-text"
                  >
                    {{ heading.title }}
                  </h2>
                </div>
              </div>
            </slot>
          </div>
          <!-- scrollable, white pane area of the dialog -->
          <div
            class="go-scrollbar flex min-h-0 grow flex-col gap-4 overflow-y-auto rounded-lg bg-surface-primary pt-4"
            data-test="permissions-dialog-scroll-container"
          >
            <template v-if="$slots.share">
              <!--share section-->
              <slot
                name="share"
                :label-id="DIALOG_LABEL_ID"
              />

              <DividerLine color="subtle" />
            </template>
            <!-- other -->

            <PermissionsDialogInviteForm
              :members="members"
              :allowed-roles="allowedInviteRoles"
              class="px-4"
              @invite="(userIds, role) => $emit('invite', userIds, role)"
            />
            <div class="px-4">
              <DefaultPermission
                :access-type="accessType"
                @update="$emit('change:access-type', $event)"
              />
            </div>
            <div class="grow px-4">
              <ul>
                <PermissionsDialogMember
                  v-for="member in sortedMembers"
                  :key="member.id"
                  :is-current-user="member.id === userStore.user?.id"
                  :member="member"
                  :allowed-roles="allowedUpdateRoles"
                  @change:role="$emit('change:user-role', member.id, $event)"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ModalDialog>
</template>
